import type { PagingResult, UsePager } from "@/admin/pager.ts";
import { DISABLED } from "@/components/Action.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { type BaseProps, mcn } from "@/utils/baseProps.ts";
import { plural } from "@/utils/plural.tsx";
import type { RefObject } from "react";

export interface PagerProps extends BaseProps {
    pager: UsePager;
    result: PagingResult | undefined;
    /** Reference to an element to scroll to top of (when the page changes) */
    topRef: RefObject<HTMLElement | null>;
}

export function Pager({ pager: { useVisual }, result, topRef, ...props }: PagerProps) {
    const { currentPage, lastPage, goPrev, goNext, goFirst, pageSize, total } = useVisual(result);

    function scrollToTop() {
        topRef.current?.scrollIntoView({ behavior: "instant" });
    }

    const fromNumber = (currentPage - 1) * pageSize + 1;
    const toNumber = currentPage === lastPage ? total : currentPage * pageSize;

    return (
        <div {...mcn("HStack gap-2", props)}>
            <WButton
                icon="i-lucide:chevron-first"
                action={
                    goFirst
                        ? () => {
                              goFirst();
                              scrollToTop();
                          }
                        : DISABLED
                }
            >
                First
            </WButton>
            <WButton
                icon="i-lucide:chevron-left"
                action={
                    goPrev
                        ? () => {
                              goPrev();
                              scrollToTop();
                          }
                        : DISABLED
                }
            >
                Prev
            </WButton>
            <span>
                {currentPage} / {lastPage ?? "..."}
            </span>
            <WButton
                icon="i-lucide:chevron-right"
                iconSide="right"
                action={
                    goNext
                        ? () => {
                              goNext();
                              scrollToTop();
                          }
                        : DISABLED
                }
            >
                Next
            </WButton>

            {result && (
                <div className="ml-auto">
                    Showing {fromNumber} to {toNumber}
                    {total != null && <> of {plural(total, "result", "results")}</>}
                </div>
            )}
        </div>
    );
}
